  import React, { useEffect, useState } from "react";
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
  import { Info } from "lucide-react";
  import type { FinancialData } from "../../../types/financialInterface";
  import { calculateFinancialRatios, calculateLLPFinancialRatios } from "../../../utils/financialRatios";
  import { getDefaultRecords } from "../../../utils/financialTop3consolidate";

  interface KeyRatiosSectionProps {
    financials: FinancialData[];
  }

  export const KeyRatiosSection: React.FC<KeyRatiosSectionProps> = ({ financials }) => {
    const [ratios, setRatios] = useState<any>([]);

    useEffect(() => {
      const top3Consolidated = getDefaultRecords(financials);
      const isLLPIN = top3Consolidated.some((f: any) => 
        (f as any).isLLPIN || 
        (f as any).statement_of_assets_and_liabilities || 
        (f as any).statement_of_income_and_expenditure
      );
      const calculated = isLLPIN ? calculateLLPFinancialRatios(top3Consolidated) : calculateFinancialRatios(top3Consolidated);
      setRatios(calculated);
    }, [financials]);

    const formatNumber = (num: any, decimals = 2) => {
      if (num === null || num === undefined || isNaN(num)) return "-";
      return Number(num).toFixed(decimals);
    };

    const sections = [
      {
        title: "Revenue & Profitability (Figures are represented in ₹ Cr)",
        metrics: [
          "netRevenue",
          "revenueGrowth",
          "otherIncome",
          "cogs",
          "employeeBenefitExpenses",
          "otherExpenses",
          "ebitda",
          "ebitdaPercent",
          "financeCost",
          "depreciation",
          "pat",
          "patPercent"
        ]
      },
      {
        title: "Assets & Capital Structure (Figures are represented in ₹ Cr)",
        metrics: [
          "tangibleAssets",
          "intangibleAssets",
          "capitalWIP",
          "nonCurrentAssetsExclFixed",
          "totalOutsideLiabilities",
          "tolTnwRatio",
        ]
      },
      {
        title: "Liquidity & Working Capital (Figures are represented in ₹ Cr)",
        metrics: [
          "longTermDebt",
          "shortTermDebt",
          "totalDebt",
          "currentAssets",
          "currentLiabilities",
          "currentRatio",
          "netWorkingCapital",
          "inventories",
          "tradeReceivables",
          "tradePayables",
          "cashAndCashEquivalents",
          "inventoriesHoldingPeriod",
          "debtorsHoldingPeriod",
          "creditorsHoldingPeriod",
          "drawingPower",
          "debtEquityRatio",
          "dscr"
        ]
      }
    ];

    const formatCurrency = (num: any) => {
      if (num === null || num === undefined || isNaN(num)) return "-";
      return `${Number(num).toLocaleString("en-IN", { maximumFractionDigits: 2 })} `;
    };

    const getMetricDisplayValue = (r: Record<string, any>, metric: string): string => {
      if (typeof r[metric] === "number") {
        if (metric.includes("Percent") || /\(\s*%\s*\)/.test(metric)) {
          return formatNumber(r[metric]) + "%";
        } else if (metric.includes("Ratio") || metric.toLowerCase().includes("dscr")) {
          return formatNumber(r[metric]) + "x";
        } else if (metric.toLowerCase().includes("revenuegrowth")) {
          return r[metric] === 0 ? "N/A" : formatNumber(r[metric]) + " %";
        } else {
          return formatCurrency(r[metric]);
        }
      } else if (typeof r[metric] === "string") {
        return r[metric] === "0" ? "N/A" : r[metric];
      }
    
      return "N/A";
    };

    const getLabel = (key: string): string => {
      const specialMappings: Record<string, string> = {
        ebitda: "EBITDA",
        ebitdaPercent: "EBITDA (%)",
        pat: "PAT", // add more mappings as needed
        patPercent: "PAT (%)", 
        dscr: "DSCR",
        cogs:"COGS",
        tolTnwRatio:"TOL TNW RATIO",
        nonCurrentAssetsExclFixed:"Non Current Assets (Excl Fixed Assets)",
        revenueGrowth:"Revenue Growth (%)"
      };
    
      // Check for exact match (case insensitive)
      const normalizedKey = key.toLowerCase();
      const mappedEntry = Object.entries(specialMappings).find(
        ([k]) => k.toLowerCase() === normalizedKey
      );
      if (mappedEntry) return mappedEntry[1];
    
      // Already all uppercase? Return as-is
      if (key === key.toUpperCase()) {
        return key;
      }
    
      // Default formatting (split camelCase & capitalize first word)
      return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, str => str.toUpperCase());
    };

    const formatFinancialYear = (dateStr: string) => {
      const year = new Date(dateStr).getFullYear();
      const fyStart = year - 1;
      const fyEnd = year.toString().slice(-2);
      return `FY ${fyStart} - ${fyEnd}`;
    };

    return (
      <section className="mb-6">
        {sections.map(section => (
          <div key={section.title} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <h3 className="text-xl font-bold text-[#9e7926]">{section.title}</h3>
            </div>

            <div className="bg-white rounded-lg border shadow-sm overflow-auto">
              <Table className="break-inside-avoid-page">
                <TableHeader>
                  <TableRow className="bg-gray-50 break-inside-avoid-page">
                    <TableHead className="font-semibold">Metric</TableHead>
                    {ratios.map(r => (
                      <TableHead key={r.year} className="text-right text-[11px] font-semibold"> {formatFinancialYear(r.year) }</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {section.metrics.map(metric => (
                    <TableRow key={metric} className="hover:bg-gray-50 break-inside-avoid-page">
                      <TableCell className="font-medium flex items-center gap-1">
                        {getLabel(metric)}
                        <Info className="h-3 w-3 text-gray-400" />
                      </TableCell>
                      {ratios.map(r => (
                        // <TableCell key={r.year} className="text-right">
                        //   {typeof r[metric] === "number"
                        //     ? metric.includes("Percent") || metric.includes("Ratio") || metric.includes("dscr")
                        //       ? formatNumber(r[metric]) + (metric.includes("Percent") ? "%" : "")
                        //       : formatCurrency(r[metric])
                        //     : "-"}
                        // </TableCell>

<TableCell  key={r.year} className="text-right">
{getMetricDisplayValue(r, metric)}
</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </section>
    );
  };
